import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetExternalPartnerDetailQuery,
  useSaveExternalPartnerMutation,
} from '../../../../../redux/services/externalPartners.jsx';
import { Spinner } from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import ExternalPartnersForm from '../../../../common/External/ExternalPartnersForm.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { ROLE_EXTERNAL_PARTNERS_ADMIN } from '../../../../../utils/constants/roles.js';
import { useSelector } from 'react-redux';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages';

const ExternalPartnersEdit = ({ from = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: idFromUrl } = useParams();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useUserHasRole(ROLE_EXTERNAL_PARTNERS_ADMIN);

  const id = isAdmin ? idFromUrl : user?.external_partner_id;
  const { data: externalPartnerData, isLoading } =
    useGetExternalPartnerDetailQuery(id, { skip: !id });

  const [savePartner] = useSaveExternalPartnerMutation();

  const { saveSuccess: toastSaveSuccess, apiErrorMessage } = useToastMessages();

  const initials = useMemo(() => {
    if (!externalPartnerData) return {};
    return {
      title_before: externalPartnerData?.contact_person?.title_before,
      last_name: externalPartnerData?.contact_person?.last_name,
      first_name: externalPartnerData?.contact_person?.first_name,
      title_after: externalPartnerData?.contact_person?.title_after,
      email: externalPartnerData?.contact_person?.email,
      phone_number: externalPartnerData?.contact_person?.phone_number,
      user_roles: externalPartnerData?.contact_person?.roles?.map(
        (role) => role.id,
      ),
      ...externalPartnerData,
    };
  }, [externalPartnerData]);

  const handleSubmit = async (values) => {
    try {
      await savePartner({ body: { id: values.id, ...values } }).unwrap();
      toastSaveSuccess();
      // If not on CMS page, go back one page; otherwise stay here
      if (!String(location?.pathname || '').startsWith('/cms')) {
        navigate(-1);
      }
    } catch (err) {
      apiErrorMessage?.(err);
    }
  };

  const pageTitle = idFromUrl
    ? t('Úprava externého partnera FRI')
    : from === 'profile'
      ? t('Profil externého partnera FRI')
      : t('Pridanie externého partnera FRI');

  useEffect(() => {
    document.title = pageTitle + ' - FRI CMS';
  }, []); // Runs on mount

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS title={pageTitle} />
      <ExternalPartnersForm initials={initials} onSubmit={handleSubmit} />
    </>
  );
};

export default ExternalPartnersEdit;
